import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { ModalSheet } from "@a2z/ui";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, QS_GLYPH, ic } from "../wizard-icons";
import type { Governorate } from "./InputSteps";

const K = keys.quickSetup;

/**
 * Governorate multi-select in a responsive sheet (mobile bottom-sheet / desktop
 * centered modal via `@a2z/ui` `ModalSheet`). Selection only — pricing stays in
 * the shipping step. Opens pre-checked with the currently-added areas so it
 * edits the set (adds + removes reconcile on confirm); the footer "Add (N)"
 * button confirms the whole checked set. Styled with the wizard tokens by
 * wrapping the content in `.qs-root` (the sheet renders outside it).
 */
export function GovernoratePickerSheet({
  isOpen,
  onClose,
  governorates,
  selectedIds,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  governorates: Governorate[];
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
}): ReactElement {
  const t = useTranslations();
  const dir = useLocale() === "ar" ? "rtl" : "ltr";
  const [sel, setSel] = useState<Set<string>>(() => new Set(selectedIds));
  const [query, setQuery] = useState("");

  // Re-seed the working selection from props each time the sheet (re)opens.
  // Keyed on `isOpen` only: we snapshot the incoming selection at open time and
  // let the user mutate the local working copy until they confirm.
  useEffect(() => {
    if (!isOpen) return;
    /* eslint-disable react-hooks/set-state-in-effect -- snapshot props into the local working set on open */
    setSel(new Set(selectedIds));
    setQuery("");
    /* eslint-enable react-hooks/set-state-in-effect */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filtered = useMemo(
    () =>
      governorates.filter((g) =>
        g.name.toLowerCase().includes(query.trim().toLowerCase()),
      ),
    [governorates, query],
  );

  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const allSelected = governorates.length > 0 && sel.size === governorates.length;
  const selectAllOrClear = () =>
    setSel(allSelected ? new Set() : new Set(governorates.map((g) => g.id)));
  const count = sel.size;

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="md">
      <div
        className="qs-root"
        dir={dir}
        style={{
          background: "transparent",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          maxHeight: "92vh",
        }}
      >
        {/* Header: title + select-all/clear + search */}
        <div style={{ padding: "2px 18px 12px", borderBottom: "1px solid var(--border)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 800, color: "var(--navy)" }}>
              {t(K.shipping.pickerTitle)}
            </span>
            <button
              onClick={selectAllOrClear}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--gold-ink)",
                whiteSpace: "nowrap",
              }}
            >
              {allSelected ? t(K.shipping.clearAll) : t(K.shipping.selectAll)}
            </button>
          </div>
          <div style={{ position: "relative" }}>
            <span
              style={{
                position: "absolute",
                insetInlineStart: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-3)",
                display: "flex",
              }}
            >
              {ic(Icons.search, 16)}
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(K.shipping.searchPlaceholder)}
              style={{
                width: "100%",
                padding: "11px 12px",
                paddingInlineStart: 36,
                borderRadius: 10,
                border: "1px solid var(--border-strong)",
                fontSize: 14,
                fontFamily: "inherit",
                color: "var(--text)",
                background: "var(--bg-soft)",
              }}
            />
          </div>
        </div>

        {/* Scrollable governorate list */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "10px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {filtered.map((g) => {
            const on = sel.has(g.id);
            return (
              <button
                key={g.id}
                onClick={() => toggle(g.id)}
                aria-pressed={on}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "11px 12px",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "start",
                  background: on ? "var(--sel-bg)" : "var(--surface)",
                  border: `1.5px solid ${on ? "var(--accent)" : "var(--border)"}`,
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: on ? "var(--accent)" : "transparent",
                    border: on ? "none" : "2px solid var(--border-strong)",
                    color: "var(--navy)",
                  }}
                >
                  {on && ic(Icons.check, 13, { stroke: 3 })}
                </span>
                <span style={{ color: "var(--accent-2)", display: "flex", flexShrink: 0 }}>
                  {ic(QS_GLYPH.pin, 15)}
                </span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--text)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {g.name}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: 16, textAlign: "center", color: "var(--text-3)", fontSize: 13.5 }}>
              {t(K.shipping.noMatches)}
            </div>
          )}
        </div>

        {/* Footer: confirm the checked set */}
        <div style={{ padding: "12px 18px 16px", borderTop: "1px solid var(--border)" }}>
          <button
            onClick={() => {
              onConfirm([...sel]);
              onClose();
            }}
            disabled={count === 0}
            style={{
              width: "100%",
              padding: "13px 16px",
              borderRadius: 12,
              border: "none",
              background: count ? "var(--accent)" : "var(--border-strong)",
              color: count ? "var(--on-accent)" : "var(--text-3)",
              fontSize: 15,
              fontWeight: 800,
              cursor: count ? "pointer" : "not-allowed",
              fontFamily: "inherit",
            }}
          >
            {count
              ? t(count > 1 ? K.shipping.addCountMany : K.shipping.addCountOne, { count })
              : t(K.shipping.selectAreasHint)}
          </button>
        </div>
      </div>
    </ModalSheet>
  );
}
